import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../shared/api/api.tokens';
import { REQUIRE_AUTH } from '../shared/interceptors/auth-context';
import { Product, GetProductsResponse, GetProductSalesResponse } from '../shared/interfaces';

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  getProducts(params?: {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    category?: string;
    isActive?: boolean;
  }): Observable<GetProductsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.set('searchTerm', params.searchTerm);
    if (params?.category) queryParams.set('category', params.category);
    if (params?.isActive !== undefined) queryParams.set('isActive', params.isActive.toString());

    return this.http.get<GetProductsResponse>(
      `${this.baseUrl}/api/v1/products?${queryParams.toString()}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  getProduct(productId: string): Observable<Product> {
    return this.http.get<Product>(
      `${this.baseUrl}/api/v1/products/${productId}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  createProduct(payload: {
    name: string;
    description?: string;
    sku?: string;
    category?: string;
    cost: number;
    price: number;
    stock: number;
  }): Observable<{ productId: string }> {
    return this.http.post<{ productId: string }>(
      `${this.baseUrl}/api/v1/products`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  updateProduct(
    productId: string,
    payload: {
      name?: string;
      description?: string;
      sku?: string;
      category?: string;
      cost?: number;
      price?: number;
      stock?: number;
      isActive?: boolean;
    }
  ): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl}/api/v1/products/${productId}`,
      payload,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  deleteProduct(productId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/api/v1/products/${productId}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  getProductSales(params?: {
    page?: number;
    pageSize?: number;
    startDate?: Date;
    endDate?: Date;
    productId?: string;
    clientId?: string;
  }): Observable<GetProductSalesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.pageSize) queryParams.set('pageSize', params.pageSize.toString());
    if (params?.startDate) queryParams.set('startDate', params.startDate.toISOString());
    if (params?.endDate) queryParams.set('endDate', params.endDate.toISOString());
    if (params?.productId) queryParams.set('productId', params.productId);
    if (params?.clientId) queryParams.set('clientId', params.clientId);

    return this.http.get<GetProductSalesResponse>(
      `${this.baseUrl}/api/v1/products/sales?${queryParams.toString()}`,
      {
        context: new HttpContext().set(REQUIRE_AUTH, true),
      }
    );
  }

  downloadTemplate(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/api/v1/products/template`, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
      responseType: 'blob',
    });
  }

  importProducts(file: File): Observable<{
    createdCount: number;
    updatedCount: number;
    errorCount: number;
    errors: Array<{ rowNumber: number; field: string; message: string }>;
  }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{
      createdCount: number;
      updatedCount: number;
      errorCount: number;
      errors: Array<{ rowNumber: number; field: string; message: string }>;
    }>(`${this.baseUrl}/api/v1/products/import`, formData, {
      context: new HttpContext().set(REQUIRE_AUTH, true),
    });
  }
}

